// src/pages/admin/SupplierManagementPage.js
import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Modal, TextField, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import api from '../../api/index.js';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 500 },
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const SupplierManagementPage = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [formData, setFormData] = useState({});
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [error, setError] = useState('');

    const fetchSuppliers = async () => {
        setLoading(true);
        try {
            const response = await api.get('/suppliers');
            setSuppliers(response.data);
        } catch (err) {
            setError('No se pudieron cargar los proveedores.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSuppliers();
    }, []);

    const handleOpenCreate = () => {
        setEditingSupplier(null);
        setFormData({ name: '', contact_person: '', email: '', phone: '' });
        setError('');
        setOpenModal(true);
    };

    const handleOpenEdit = (supplier) => {
        setEditingSupplier(supplier);
        setFormData({
            name: supplier.name || '',
            contact_person: supplier.contact_person || '',
            email: supplier.email || '',
            phone: supplier.phone || ''
        });
        setError('');
        setOpenModal(true);
    };

    const handleCloseModal = () => setOpenModal(false);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        setError('');
        try {
            if (editingSupplier) {
                await api.put(`/suppliers/${editingSupplier.id}`, formData);
            } else {
                await api.post('/suppliers', formData);
            }
            handleCloseModal();
            fetchSuppliers();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar el proveedor.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro? Si este proveedor está en uso, no se podrá eliminar.')) {
            try {
                await api.delete(`/suppliers/${id}`);
                fetchSuppliers();
            } catch (err) {
                alert(err.response?.data?.message || 'Error al eliminar.');
            }
        }
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 90 },
        { field: 'name', headerName: 'Nombre', width: 250 },
        { field: 'contact_person', headerName: 'Contacto', width: 200 },
        { field: 'email', headerName: 'Email', width: 250 },
        { field: 'phone', headerName: 'Teléfono', flex: 1 },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 200,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => handleOpenEdit(params.row)}>Editar</Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(params.row.id)}>Eliminar</Button>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ height: '85vh', width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Gestión de Proveedores</Typography>
                <Button variant="contained" onClick={handleOpenCreate}>Añadir Proveedor</Button>
            </Box>
            {error && !loading && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <DataGrid
                rows={suppliers}
                columns={columns}
                loading={loading}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            />
            <Modal open={openModal} onClose={handleCloseModal}>
                <Box sx={style}>
                    <Typography variant="h6">{editingSupplier ? 'Editar Proveedor' : 'Crear Proveedor'}</Typography>
                    <TextField margin="normal" required fullWidth autoFocus label="Nombre" name="name" value={formData.name || ''} onChange={handleInputChange} />
                    <TextField margin="normal" fullWidth label="Persona de Contacto" name="contact_person" value={formData.contact_person || ''} onChange={handleInputChange} />
                    <TextField margin="normal" fullWidth label="Email" name="email" value={formData.email || ''} onChange={handleInputChange} />
                    <TextField margin="normal" fullWidth label="Teléfono" name="phone" value={formData.phone || ''} onChange={handleInputChange} />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseModal} sx={{ mr: 1 }}>Cancelar</Button>
                        <Button variant="contained" onClick={handleSubmit}>Guardar</Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default SupplierManagementPage;